import { type OptionsType, deleteCookie } from 'cookies-next'
import type { User as AuthUser, UserInfo } from 'firebase/auth'
import { usePostHog } from 'posthog-js/react'
import { useMemo } from 'react'
import { useAuthState } from 'react-firebase-hooks/auth'
import {
  DUPE_AUTH_USER_ID_KEY,
  DUPE_FLAGS_COOKIE_KEY,
  TEMP_USER_ID_KEY,
} from '~/src/lib/constants'
import auth from '~/src/lib/firebase/firebaseAuth'

export interface AuthUserWithToken extends AuthUser {
  accessToken?: string
}

type Response = {
  authUser?: AuthUserWithToken | null | undefined
  isAdmin: boolean
  loading: boolean
  error: Error | undefined
  signOut: () => Promise<void>
  authUserNotAnonymous: boolean
}

function getPropertyFromProviderData(
  property: keyof UserInfo,
  authUser: AuthUserWithToken
) {
  return (
    authUser?.[property] ||
    (authUser?.providerData || []).find((pd) => pd[property])?.[property] ||
    null
  )
}

const ADMIN_EMAILS = [
  'i8ramin@gmail.com',
  'arnabg@gmail.com',
  'mariano.selvaggi81@gmail.com',
  'mariano.selvaggi.81@gmail.com',
  'gabilhartman@gmail.com',
]

/**
 * Custom hook for managing Firebase authentication state and user data.
 *
 * This hook provides a comprehensive interface for authentication state management,
 * including user data, admin status, loading states, and sign-out functionality.
 *
 * @returns {Object} Authentication state and utilities
 * @returns {AuthUserWithToken | null | undefined} authUser - The current authenticated user with enhanced properties
 * @returns {boolean} isAdmin - Whether the current user has admin privileges
 * @returns {boolean} loading - Whether the authentication state is currently loading
 * @returns {Error | undefined} error - Any authentication error that occurred
 * @returns {() => Promise<void>} signOut - Function to sign out the current user and clear all auth data
 * @returns {boolean} authUserNotAnonymous - Whether the user is authenticated and not anonymous
 *
 * @example
 * ```tsx
 * const { authUser, loading, isAdmin, signOut } = useAuthUser()
 *
 * if (loading) return <div>Loading...</div>
 * if (!authUser) return <div>Please sign in</div>
 *
 * return (
 *   <div>
 *     <p>Welcome, {authUser.displayName}</p>
 *     {isAdmin && <AdminPanel />}
 *     <button onClick={signOut}>Sign Out</button>
 *   </div>
 * )
 * ```
 */
const useAuthUser = (): Response => {
  const posthog = usePostHog()
  const [authUser, loading, error] = useAuthState(auth)
  const isAdmin =
    authUser?.email?.endsWith('@addtocarrot.com') ||
    authUser?.email?.endsWith('@carrot.link') ||
    authUser?.email?.endsWith('@dupe.com') ||
    ADMIN_EMAILS.includes(authUser?.email || '') ||
    false

  const enhancedAuthUser = useMemo(
    () =>
      authUser
        ? ({
            ...authUser,
            displayName: getPropertyFromProviderData('displayName', authUser),
            photoURL: getPropertyFromProviderData('photoURL', authUser),
          } as AuthUserWithToken)
        : undefined,
    [authUser]
  )

  const signOut = async () => {
    const cookieOptions: OptionsType = {
      path: '/',
    }

    if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
      cookieOptions.domain = '.dupe.com'
    }

    try {
      deleteCookie(DUPE_AUTH_USER_ID_KEY, cookieOptions)
      deleteCookie(TEMP_USER_ID_KEY, cookieOptions)
      deleteCookie(DUPE_FLAGS_COOKIE_KEY, cookieOptions)
      window.localStorage.removeItem(TEMP_USER_ID_KEY)
    } catch (error) {
      console.error('Error signing out:', error)
    }

    // Reset posthog (https://posthog.com/docs/libraries/js/features#resetting-a-user)
    posthog.reset()

    await auth.signOut()
  }

  const authUserNotAnonymous = !!authUser && !authUser.isAnonymous && !loading

  return {
    authUser: enhancedAuthUser,
    isAdmin,
    loading,
    error,
    signOut,
    authUserNotAnonymous,
  }
}

export default useAuthUser
